import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Card, Label, TextInput, Textarea } from "flowbite-react";
import { useForm, Controller } from "react-hook-form";
import { useGetData } from "../../../../../hooks/useGetData";
import { useMutationData } from "../../../../../hooks/useMutationData";
import SkyClear from "../../../../../img/SkyClear.png";
import { EvaluadoInterface } from "../../../../../interfaces/EvaluadoInterface";
import { FechasInterface } from "../../../../../interfaces/FechasInterface";
import { ReferenciasLaboralesInterface } from "../../../../../interfaces/ReferenciasLaboralesInterface";
import { NotasRLInterface } from "../../../../../interfaces/NotasRLInterface";
import { Loading } from "../../../../../components/Loading/Loading";
import Header from "../../../../../components/HeaderForms/HeaderForms";
import { LaboralesListaPrint } from "./LaboralesListaPrint";
import { ThemeContext } from "../../../../../components/ThemeContext/ThemeContext";

export const LaboralesEditarScreen = () => {
  const { isDarkMode } = useContext(ThemeContext);
  const tituloFormulario = "REFERENCIAS LABORALES";
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [referenciasLaborales, setReferenciasLaborales] = useState([
    {
      company2: "",
      address: "",
      phone: "",
      immediateSupervisor: "",
      supervisorPosition: "",
      startDate: "",
      initialPosition: "",
      initialSalary: "",
      finalPosition: "",
      finalSalary: "",
      endDate: "",
      reasonForLeaving: "",
      notes: "",
    },
  ]);
  const { evaluadosList_id } = useParams<{ evaluadosList_id: string }>();
  const [fechaNacimiento, setFechaNacimiento] = useState<string>("");
  const [edad, setEdad] = useState<number | null>(null);
  const { control, handleSubmit, setValue, reset } = useForm();
  const [formData, setFormData] = useState({
    visitDate: "",
    name: "",
  });

  const calcularEdad = (fechaNacimiento: string) => {
    const fechaNac = new Date(fechaNacimiento);
    const hoy = new Date();
    let edadCalculada = hoy.getFullYear() - fechaNac.getFullYear();
    const mes = hoy.getMonth() - fechaNac.getMonth();

    if (mes < 0 || (mes === 0 && hoy.getDate() < fechaNac.getDate())) {
      edadCalculada--;
    }

    return edadCalculada;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleReferenciaChange = (
    index: number,
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setReferenciasLaborales((prev) =>
      prev.map((referencia, i) =>
        i === index ? { ...referencia, [name]: value } : referencia
      )
    );
  };

  const handleFechaNacimientoChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const nuevaFechaNacimiento = event.target.value;
    setFechaNacimiento(nuevaFechaNacimiento);

    const nuevaEdad = calcularEdad(nuevaFechaNacimiento);
    setEdad(nuevaEdad);
  };

  const handlePrint = () => {
    const printContent = document.getElementById("printableContent");
  
    if (printContent) {
      const originalContent = document.body.innerHTML;
  
      const printWindow = window.open('', '', 'width=800,height=600');
  
      if (printWindow) {
        printWindow.document.write('<html><head><title>Vista Previa de Impresión</title>');

        const styles = document.getElementsByTagName("style");
        for (let i = 0; i < styles.length; i++) {
          printWindow.document.write(styles[i].outerHTML);
        }
        printWindow.document.write('</head><body>');
        printWindow.document.write(printContent.innerHTML);
        printWindow.document.write('</body></html>');
        printWindow.document.close();
        printWindow.onload = () => {
          printWindow.print();
          printWindow.close();
        };
      } else {
        alert("No se pudo abrir la ventana de impresión. Verifique las configuraciones del navegador.");
      }
    }
  };

  const renderHeader = () => (
    <>
      {/* Header */}
      <Header titulo={tituloFormulario} />
    </>
  );

  const queryEvaluado = useGetData<EvaluadoInterface>({
    url: `/evaluado/${evaluadosList_id}`,
    name: ["evaluado"],
  });

  const queryFecha = useGetData<FechasInterface[]>({
    url: `/evaluado/${evaluadosList_id}/fecha-visita-bienesinmuebles`,
    name: ["fecha-visita-bienesinmuebles"],
  });

  const mutateFecha = useMutationData({
    url: `/evaluado/${evaluadosList_id}/fecha-visita-bienesinmuebles`,
    reload: ["evaluado", `${evaluadosList_id}`, "fecha-visita-bienesinmuebles"],
  });

  const queryLaborales = useGetData<ReferenciasLaboralesInterface>({
    url: `/referencia-laboral/${evaluadosList_id}`,
    name: ["evaluado", `${evaluadosList_id}`, "referencia-laboral"],
  });

  const mutateReferenciaLaboral = useMutationData({
    url: `/referencia-laboral/${evaluadosList_id}`,
    reload: ["evaluado", "referencia-laboral", `${evaluadosList_id}`],
  });

  const queryNotasRL = useGetData<NotasRLInterface[]>({
    url: `/notas_rl/${evaluadosList_id}`,
    name: ["evaluado", `${evaluadosList_id}`, "notas_rl"],
  });

  const mutateNotasrl = useMutationData({
    url: `/notas_rl/${evaluadosList_id}`,
    reload: ["evaluado", "notas_rl", `${evaluadosList_id}`],
  });

  const generoMap: { [key: string]: string } = {
    M: "Masculino",
    F: "Femenino",
    O: "Otro",
  };

  const generoTexto =
    generoMap[queryEvaluado.data?.genero as string] || "No especificado";

  useEffect(() => {
    if (queryEvaluado.data) {
      const fechaNac = queryEvaluado.data.fecha_nacimiento || "";
      setFechaNacimiento(fechaNac);

      const nuevaEdad = calcularEdad(fechaNac);
      setEdad(nuevaEdad);

      reset({
        nombre: queryEvaluado.data.nombre || "",
        apellido_paterno: queryEvaluado.data.apellido_paterno || "",
        apellido_materno: queryEvaluado.data.apellido_materno || "",
        rfc: queryEvaluado.data.rfc || "",
        genero: queryEvaluado.data.genero || "",
        estado_civil: queryEvaluado.data.estado_civil || "",
        escolaridad: queryEvaluado.data.escolaridad || "",
        fecha_nacimiento: fechaNac,
        curp: queryEvaluado.data.curp || "",
        empresa: queryEvaluado.data.empresa?.nombre || "",
        puesto: queryEvaluado.data.puesto || "",
        fecha_ingreso: queryEvaluado.data.fecha_ingreso || "",
      });
    }
  }, [queryEvaluado.data, reset]);

  useEffect(() => {
    if (queryLaborales.data) {
      const rl = queryLaborales.data;
      reset({
        empresa_rl1: rl.empresa_rl1 || "",
        domicilio_rl1: rl.domicilio_rl1 || "",
        telefono_rl1: rl.telefono_rl1 || "",
        nombre_jefe_rl1: rl.nombre_jefe_rl1 || "",
        puesto_jefe_rl1: rl.puesto_jefe_rl1 || "",
        fecha_ingreso_rl1: rl.fecha_ingreso_rl1 || "",
        puesto_inicial_rl1: rl.puesto_inicial_rl1 || "",
        sueldo_inicial_rl1: rl.sueldo_inicial_rl1 || "",
        puesto_final_rl1: rl.puesto_final_rl1 || "",
        sueldo_final_rl1: rl.sueldo_final_rl1 || "",
        fecha_salida_rl1: rl.fecha_salida_rl1 || "",
        motivo_separacion_rl1: rl.motivo_separacion_rl1 || "",
        empresa_rl2: rl.empresa_rl2 || "",
        domicilio_rl2: rl.domicilio_rl2 || "",
        telefono_rl2: rl.telefono_rl2 || "",
        nombre_jefe_rl2: rl.nombre_jefe_rl2 || "",
        puesto_jefe_rl2: rl.puesto_jefe_rl2 || "",
        fecha_ingreso_rl2: rl.fecha_ingreso_rl2 || "",
        puesto_inicial_rl2: rl.puesto_inicial_rl2 || "",
        sueldo_inicial_rl2: rl.sueldo_inicial_rl2 || "",
        puesto_final_rl2: rl.puesto_final_rl2 || "",
        sueldo_final_rl2: rl.sueldo_final_rl2 || "",
        fecha_salida_rl2: rl.fecha_salida_rl2 || "",
        motivo_separacion_rl2: rl.motivo_separacion_rl2 || "",
        empresa_rl3: rl.empresa_rl3 || "",
        domicilio_rl3: rl.domicilio_rl3 || "",
        telefono_rl3: rl.telefono_rl3 || "",
        nombre_jefe_rl3: rl.nombre_jefe_rl3 || "",
        puesto_jefe_rl3: rl.puesto_jefe_rl3 || "",
        fecha_ingreso_rl3: rl.fecha_ingreso_rl3 || "",
        puesto_inicial_rl3: rl.puesto_inicial_rl3 || "",
        sueldo_inicial_rl3: rl.sueldo_inicial_rl3 || "",
        puesto_final_rl3: rl.puesto_final_rl3 || "",
        sueldo_final_rl3: rl.sueldo_final_rl3 || "",
        fecha_salida_rl3: rl.fecha_salida_rl3 || "",
        motivo_separacion_rl3: rl.motivo_separacion_rl3 || "",
      });
    }
  }, [queryLaborales.data, reset]);

  useEffect(() => {
    if (queryNotasRL.data && queryNotasRL.data.length > 0) {
      const registroValido = queryNotasRL.data.find((item) => item.nota_rl);

      if (registroValido) {
        setValue("nota_rl", registroValido.nota_rl);
      }
    }
  }, [queryNotasRL.data, setValue]);

  if (queryEvaluado.isLoading) {
    return <Loading />;
  }

  const renderPage1 = () => (
    <div className={`space-y-4 p-4 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}>
      {/* Fecha Visita */}
      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label htmlFor="fecha_refl" value="FECHA VISITA:" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`} />
          <Controller
            name="fecha_refl"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextInput
                id="fecha_refl"
                name="fecha_refl"
                type="date"
                value={
                  queryFecha.data && queryFecha.data.length > 0
                    ? queryFecha.data[0].fecha_refl || ""
                    : ""
                }
                className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-white text-gray-800 border-gray-300"}`}
              />
            )}
          />
        </div>
        {/* Campo para nombre */}
        <div className="col-span-2">
          <Label htmlFor="name" value="NOMBRE:" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`} />
          <TextInput
            id="name"
            name="name"
            value={`${queryEvaluado.data?.nombre || ""} ${
              queryEvaluado.data?.apellido_paterno || ""
            } ${queryEvaluado.data?.apellido_materno || ""}`}
            disabled={true}
            onChange={handleInputChange}
            className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
          />
        </div>
      </div>
      {/* Campo para RFC */}
      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label htmlFor="rfc" value="RFC:" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`} />
          <TextInput
            id="rfc"
            name="rfc"
            value={queryEvaluado.data?.rfc || ""}
            disabled={true}
            onChange={handleInputChange}
            className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
          />
        </div>
        {/* Edad */}
        <div className="col-span-1">
          <Label htmlFor="edad">EDAD:</Label>
          <TextInput
            id="edad"
            name="edad"
            value={edad !== null ? edad.toString() : ""}
            disabled={true}
            className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
          />
        </div>
        {/* Género */}
        <div>
          <Label htmlFor="sex" value="SEXO:" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`} />
          <TextInput
            id="sex"
            name="sex"
            value={generoTexto}
            disabled={true}
            onChange={handleInputChange}
            className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
          />
        </div>
        {/* Estado Civil */}
        <div>
          <Label htmlFor="civilStatus" value="ESTADO CIVIL:" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`} />
          <TextInput
            id="civilStatus"
            name="civilStatus"
            value={queryEvaluado.data?.estado_civil || ""}
            disabled={true}
            onChange={handleInputChange}
            className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
          />
        </div>
        {/* Escolaridad */}
        <div>
          <Label htmlFor="education" value="ESCOLARIDAD:" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`} />
          <TextInput
            id="education"
            name="education"
            value={queryEvaluado.data?.escolaridad || ""}
            disabled={true}
            onChange={handleInputChange}
            className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
          />
        </div>
        {/* Fecha de Nacimiento */}
        <div>
          <Label htmlFor="birthDate" value="FECHA DE NACIMIENTO:" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`} />
          <TextInput
            id="birthDate"
            name="birthDate"
            type="date"
            value={queryEvaluado.data?.fecha_nacimiento || ""}
            disabled={true}
            onChange={handleInputChange}
            className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
          />
        </div>
        {/* CURP */}
        <div>
          <Label htmlFor="curp" value="CURP:" className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`} />
          <TextInput
            id="curp"
            name="curp"
            value={queryEvaluado.data?.curp || ""}
            disabled={true}
            onChange={handleInputChange}
            className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
          />
        </div>
        {/* Empresa */}
        <div className="col-span-1">
          <Label htmlFor="empresa">EMPRESA:</Label>
          <TextInput
            id="empresa_id"
            name="empresa"
            value={queryEvaluado.data?.empresa_id?.nombre || ""}
            onChange={handleInputChange}
            disabled={true}
            className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-800"}`}
          />
        </div>
      </div>
    </div>
  );

  const renderPage2 = (index: number) => (
    <div className={`space-y-4 p-4 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}>
      {[...Array(3)].map((_, index) => (
        <div key={index}>
          <h2 className={`text-xl font-bold mb-4 p-2 rounded-t ${isDarkMode ? "bg-blue-800 text-white" : "bg-customBlue text-white"}`}>
            REFERENCIA LABORAL {index + 1}{" "}
          </h2>
          <div className="space-y-4">
            <div className="flex flex-col text-left">
              <Label
                htmlFor={`empresa_rl${index + 1}`}
                value="EMPRESA:"
                className={`text-left ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
              />
              <Controller
                name={`empresa_rl${index + 1}`}
                control={control}
                defaultValue={
                  queryLaborales.data
                    ? queryLaborales.data[
                        `empresa_rl${
                          index + 1
                        }` as keyof ReferenciasLaboralesInterface
                      ] || ""
                    : ""
                }
                render={({ field }) => (
                  <input
                    type="text"
                    id={`empresa_rl${index + 1}`}
                    {...field}
                    onChange={(e) => field.onChange(e.target.value)}
                    className={`text-left w-auto border rounded ${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-white text-gray-800 border-gray-300"}`}
                  />
                )}
              />
            </div>
            <div className="flex flex-col text-left">
              <Label
                htmlFor="domicilio_rl1"
                value="DOMICILIO:"
                className={`text-left ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
              />
              <Controller
                name={`domicilio_rl${index + 1}`}
                control={control}
                defaultValue={
                  queryLaborales.data
                    ? queryLaborales.data[
                        `domicilio_rl${
                          index + 1
                        }` as keyof ReferenciasLaboralesInterface
                      ] || ""
                    : ""
                }
                render={({ field }) => (
                  <input
                    type="text"
                    id={`domicilio_rl${index + 1}`}
                    {...field}
                    onChange={(e) => field.onChange(e.target.value)}
                    className={`text-left w-auto border rounded ${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-white text-gray-800 border-gray-300"}`}
                  />
                )}
              />
            </div>
            <div className="flex flex-col text-left">
              <Label
                htmlFor={`telefono_rl${index + 1}`}
                value="TELÉFONO:"
                className={`text-left ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
              />
              <Controller
                name={`telefono_rl${index + 1}`}
                control={control}
                defaultValue={
                  queryLaborales.data
                    ? queryLaborales.data[
                        `telefono_rl${
                          index + 1
                        }` as keyof ReferenciasLaboralesInterface
                      ] || ""
                    : ""
                }
                render={({ field }) => (
                  <input
                    type="text"
                    id={`telefono_rl${index + 1}`}
                    {...field}
                    onChange={(e) => field.onChange(e.target.value)}
                    className={`text-left w-auto border rounded ${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-white text-gray-800 border-gray-300"}`}
                  />
                )}
              />
            </div>
            <div className="flex flex-col text-left">
              <Label
                htmlFor={`nombre_jefe_rl${index + 1}`}
                value="NOMBRE DEL JEFE INMEDIATO:"
                className={`text-left ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
              />
              <Controller
                name={`nombre_jefe_rl${index + 1}`}
                control={control}
                defaultValue={
                  queryLaborales.data
                    ? queryLaborales.data[
                        `nombre_jefe_rl${
                          index + 1
                        }` as keyof ReferenciasLaboralesInterface
                      ] || ""
                    : ""
                }
                render={({ field }) => (
                  <input
                    type="text"
                    id={`nombre_jefe_rl${index + 1}`}
                    {...field}
                    onChange={(e) => field.onChange(e.target.value)}
                    className={`text-left w-auto border rounded ${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-white text-gray-800 border-gray-300"}`}
                  />
                )}
              />
            </div>
            <div className="flex flex-col text-left">
              <Label
                htmlFor={`puesto_jefe_rl${index + 1}`}
                value="PUESTO DEL JEFE INMEDIATO:"
                className={`text-left ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
              />
              <Controller
                name={`puesto_jefe_rl${index + 1}`}
                control={control}
                defaultValue={
                  queryLaborales.data
                    ? queryLaborales.data[
                        `puesto_jefe_rl${
                          index + 1
                        }` as keyof ReferenciasLaboralesInterface
                      ] || ""
                    : ""
                }
                render={({ field }) => (
                  <input
                    type="text"
                    id={`puesto_jefe_rl${index + 1}`}
                    {...field}
                    onChange={(e) => field.onChange(e.target.value)}
                    className={`text-left w-auto border rounded ${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-white text-gray-800 border-gray-300"}`}
                  />
                )}
              />
            </div>
            <div className="flex flex-col text-left">
              <Label
                htmlFor={`fecha_ingreso_rl${index + 1}`}
                value="FECHA DE INGRESO:"
                className={`text-left ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
              />
              <Controller
                name={`fecha_ingreso_rl${index + 1}`}
                control={control}
                defaultValue={
                  queryLaborales.data
                    ? queryLaborales.data[
                        `fecha_ingreso_rl${
                          index + 1
                        }` as keyof ReferenciasLaboralesInterface
                      ] || ""
                    : ""
                }
                render={({ field }) => (
                  <input
                    type="date"
                    id={`fecha_ingreso_rl${index + 1}`}
                    {...field}
                    onChange={(e) => field.onChange(e.target.value)}
                    className={`text-left w-auto border rounded ${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-white text-gray-800 border-gray-300"}`}
                  />
                )}
              />
            </div>
            <div className="flex flex-col text-left">
              <Label
                htmlFor={`puesto_inicial_rl${index + 1}`}
                value="PUESTO INICIAL:"
                className={`text-left ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
              />
              <Controller
                name={`puesto_inicial_rl${index + 1}`}
                control={control}
                defaultValue={
                  queryLaborales.data
                    ? queryLaborales.data[
                        `puesto_inicial_rl${
                          index + 1
                        }` as keyof ReferenciasLaboralesInterface
                      ] || ""
                    : ""
                }
                render={({ field }) => (
                  <input
                    type="text"
                    id={`puesto_inicial_rl${index + 1}`}
                    {...field}
                    onChange={(e) => field.onChange(e.target.value)}
                    className={`text-left w-auto border rounded ${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-white text-gray-800 border-gray-300"}`}
                  />
                )}
              />
            </div>
            <div className="flex flex-col text-left">
              <Label
                htmlFor={`sueldo_inicial_rl${index + 1}`}
                value="SUELDO INICIAL:"
                className={`text-left ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
              />
              <Controller
                name={`sueldo_inicial_rl${index + 1}`}
                control={control}
                defaultValue={
                  queryLaborales.data
                    ? queryLaborales.data[
                        `sueldo_inicial_rl${
                          index + 1
                        }` as keyof ReferenciasLaboralesInterface
                      ] || ""
                    : ""
                }
                render={({ field }) => (
                  <input
                    type="text"
                    id={`sueldo_inicial_rl${index + 1}`}
                    {...field}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^\d.-]/g, "");
                      field.onChange(value);
                    }}
                    inputMode="decimal"
                    className={`text-left w-auto border rounded ${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-white text-gray-800 border-gray-300"}`}
                  />
                )}
              />
            </div>
            <div className="flex flex-col text-left">
              <Label
                htmlFor={`puesto_final_rl${index + 1}`}
                value="PUESTO FINAL:"
                className={`text-left ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
              />
              <Controller
                name={`puesto_final_rl${index + 1}`}
                control={control}
                defaultValue={
                  queryLaborales.data
                    ? queryLaborales.data[
                        `puesto_final_rl${
                          index + 1
                        }` as keyof ReferenciasLaboralesInterface
                      ] || ""
                    : ""
                }
                render={({ field }) => (
                  <input
                    type="text"
                    id={`puesto_final_rl${index + 1}`}
                    {...field}
                    onChange={(e) => field.onChange(e.target.value)}
                    className={`text-left w-auto border rounded ${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-white text-gray-800 border-gray-300"}`}
                  />
                )}
              />
            </div>
            <div className="flex flex-col text-left">
              <Label
                htmlFor={`sueldo_final_rl${index + 1}`}
                value="SUELDO FINAL:"
                className={`text-left ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
              />
              <Controller
                name={`sueldo_final_rl${index + 1}`}
                control={control}
                defaultValue={
                  queryLaborales.data
                    ? queryLaborales.data[
                        `sueldo_final_rl${
                          index + 1
                        }` as keyof ReferenciasLaboralesInterface
                      ] || ""
                    : ""
                }
                render={({ field }) => (
                  <input
                    type="text"
                    id={`sueldo_final_rl${index + 1}`}
                    {...field}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^\d.-]/g, "");
                      field.onChange(value);
                    }}
                    inputMode="decimal"
                    className={`text-left w-auto border rounded ${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-white text-gray-800 border-gray-300"}`}
                  />
                )}
              />
            </div>
            <div className="flex flex-col text-left">
              <Label
                htmlFor={`fecha_salida_rl${index + 1}`}
                value="FECHA DE SALIDA:"
                className={`text-left ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
              />
              <Controller
                name={`fecha_salida_rl${index + 1}`}
                control={control}
                defaultValue={
                  queryLaborales.data
                    ? queryLaborales.data[
                        `fecha_salida_rl${
                          index + 1
                        }` as keyof ReferenciasLaboralesInterface
                      ] || ""
                    : ""
                }
                render={({ field }) => (
                  <input
                    type="date"
                    id={`fecha_salida_rl${index + 1}`}
                    {...field}
                    onChange={(e) => field.onChange(e.target.value)}
                    className={`text-left w-auto border rounded ${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-white text-gray-800 border-gray-300"}`}
                  />
                )}
              />
            </div>
            <div className="flex flex-col text-left">
              <Label
                htmlFor={`motivo_separacion_rl${index + 1}`}
                value="MOTIVO DE LA SEPARACIÓN:"
                className={`text-left ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
              />
              <Controller
                name={`motivo_separacion_rl${index + 1}`}
                control={control}
                defaultValue={
                  queryLaborales.data
                    ? queryLaborales.data[
                        `motivo_separacion_rl${
                          index + 1
                        }` as keyof ReferenciasLaboralesInterface
                      ] || ""
                    : ""
                }
                render={({ field }) => (
                  <Textarea
                    id={`motivo_separacion_rl${index + 1}`}
                    {...field}
                    onChange={(e) => handleReferenciaChange(index, e)}
                    rows={6}
                    className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-white text-gray-800 border-gray-300"}`}
                  />
                )}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const addReferenciaLaboral = () => {
    setReferenciasLaborales([
      ...referenciasLaborales,
      {
        company2: "",
        address: "",
        phone: "",
        immediateSupervisor: "",
        supervisorPosition: "",
        startDate: "",
        initialPosition: "",
        initialSalary: "",
        finalPosition: "",
        finalSalary: "",
        endDate: "",
        reasonForLeaving: "",
        notes: "",
      },
    ]);
  };

  const validateAndRemoveEmptyReferences = () => {
    setReferenciasLaborales((prev) =>
      prev.filter((referencia, index) => {
        if (index === 0) return true;
        const isFilled = Object.values(referencia).some(
          (value) => value.trim() !== ""
        );
        return isFilled;
      })
    );
  };

  const handlePageChange = (direction: "next" | "prev") => {
    validateAndRemoveEmptyReferences();
    if (direction === "next") {
      setCurrentPage((prev) => prev + 1);
    } else if (direction === "prev") {
      setCurrentPage((prev) => prev - 1);
    }
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}>
      <div className={`container mx-auto p-4`}>
        <form
          onSubmit={handleSubmit((data) => {
            const formData = {
              ...data,
            };

            mutateFecha.mutate(
              {
                data: { fecha_refl: data.fecha_refl },
                tipo: "put",
              },
              {
                onSuccess: () => navigate(`/domiciliaria/${evaluadosList_id}`),
                onError: (error) =>
                  console.error(
                    "Error al modificar la fecha de la Referencia Laboral:",
                    error
                  ),
              }
            );

            mutateReferenciaLaboral.mutate(
              {
                data: formData,
                tipo: "put",
              },
              {
                onSuccess: () => {
                  navigate(`/domiciliaria/${evaluadosList_id}`);
                },
                onError: (error) =>
                  console.error(
                    "Error al modificar la Referencia Laboral:",
                    error
                  ),
              }
            );

            mutateNotasrl.mutate(
              {
                data: formData,
                tipo: "put",
              },
              {
                onSuccess: () => {
                  navigate(`/domiciliaria/${evaluadosList_id}`);
                },
                onError: (error) =>
                  console.error(
                    "Error al modificar las notas de Referencia Laboral:",
                    error
                  ),
              }
            );
          })}
          className={`max-w-4xl mx-auto rounded-lg shadow-lg overflow-hidden ${isDarkMode ? "bg-gray-800" : "bg-white"}`}
        >
          {renderHeader()}
          {currentPage === 1 && renderPage1()}
          {currentPage === 2 && (
            <div>
              {referenciasLaborales.map((_, index) => (
                <div key={index} className={`referencia-${index}`}>
                  {renderPage2(index)}
                </div>
              ))}
              <h2 className={`text-xl font-bold mb-4 p-2 rounded-t m-4 ${isDarkMode ? "bg-blue-800 text-white" : "bg-customBlue text-white"}`}>
                NOTAS
              </h2>
              <div className={`space-y-4 m-4 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}>
                <Controller
                  name="nota_rl"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Textarea
                      id="nota_rl"
                      {...field}
                      name="nota_rl"
                      rows={6}
                      onInput={(e) => {
                        const target = e.target as HTMLTextAreaElement;
                        target.style.height = "auto";
                        target.style.height = `${target.scrollHeight}px`;
                      }}
                      className={`mt-2 p-2 border rounded-md resize-none ${isDarkMode ? "bg-gray-700 border-gray-600 text-gray-300" : "border-gray-300"}`}
                      style={{ height: "auto", overflow: "hidden" }}
                    />
                  )}
                />
              </div>
              <div className={`m-4 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                <p className="text-left">
                  {" "}
                  Bajo protesta de decir verdad, hago constar que los datos
                  proporcionados son verdaderos.{" "}
                </p>
                <p className="text-left">
                  Asimismo, autorizo a la empresa SKA TRACKING AND SECURITY S.A.
                  de C.V. para que proporcione toda la Información, documentos y
                  todos los datos que derive de las evaluaciones de pruebas de
                  confianza que se me realicen.{" "}
                </p>
              </div>
            </div>
          )}
          {/* Botones de navegación */}
          <div className="mt-6 p-4 flex justify-between">
            {currentPage < 2 && (
              <Button
                color="red"
                onClick={() => {
                  window.scrollTo(0, 0);
                  navigate(`/domiciliaria/${evaluadosList_id}`);
                }}
                className={`${isDarkMode ? "bg-red-600 hover:bg-red-700 text-white" : "bg-red-500 text-white"}`}
              >
                Cancelar
              </Button>
            )}
            {currentPage > 1 && (
              <Button
                onClick={() => {
                  window.scrollTo(0, 0);
                  handlePageChange("prev");
                }}
                className={`${isDarkMode ? "bg-violet-600 hover:bg-violet-900 text-white" : "bg-violet-400 text-white"}`}
              >
                Anterior
              </Button>
            )}
            {currentPage < 2 ? (
              <Button
                color="blue"
                onClick={() => {
                  window.scrollTo(0, 0);
                  handlePageChange("next");
                }}
                className={`${isDarkMode ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-blue-500 text-white"}`}
              >
                Siguiente
              </Button>
            ) : (
              <div className="flex space-x-4">
                <button
                  type="submit"
                  className={`rounded px-4 py-2 ${isDarkMode ? "bg-green-600 hover:bg-green-700 text-white" : "bg-green-500 text-white"}`}
                  onClick={validateAndRemoveEmptyReferences}
                >
                  Guardar
                </button>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};
